import React, { useEffect, useState } from "react";
import api from "../../services/apiService";
import { ALL_STATUSES, getStatusColors } from "./performanceUtils";
import SpotlightSection from "./SpotlightSection";
import LeaderboardSection from "./LeaderboardSection";
import PerformanceQuadrant from "./PerformanceQuadrant";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return { Authorization: token ? `Bearer ${token}` : undefined };
};

function PerformanceDashboard() {
  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [performanceData, setPerformanceData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await api.get("/projects", { headers: authHeaders() });
        const list = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
            ? res.data
            : [];
        setProjects(list);
      } catch {
        // silently ignore — project filter just shows "All Projects" only
      }
    };
    fetchProjects();
  }, []);

  useEffect(() => {
    const fetchPerformance = async () => {
      setIsLoading(true);
      try {
        const params = selectedProjectId
          ? { projectId: selectedProjectId }
          : {};
        const res = await api.get("/performance-summary", {
          params,
          headers: authHeaders(),
        });
        setPerformanceData(Array.isArray(res?.data) ? res.data : []);
      } catch {
        setPerformanceData([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPerformance();
  }, [selectedProjectId]);

  return (
    <div className=" w-full max-w-full space-y-6">
      <p className="text-sm text-app-muted">
        Screener responses received = output. Everything else = effort.
        Conversion rate = output ÷ effort.
      </p>

      {/* Controls + Legend row */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        {/* Project filter */}
        <select
          value={selectedProjectId ?? ""}
          onChange={(e) =>
            setSelectedProjectId(e.target.value ? Number(e.target.value) : null)
          }
          className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 outline-none transition-colors hover:border-gray-400 focus:border-[#2470B7] focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-slate-500 dark:focus:border-sky-500 dark:focus:ring-sky-500/30 [color-scheme:light] dark:[color-scheme:dark]"
        >
          <option value="">All Projects</option>
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.projectName}
            </option>
          ))}
        </select>

        {/* Legend */}
        <div className="flex flex-wrap gap-2">
          {ALL_STATUSES.map((status) => {
            const colors = getStatusColors(status);
            return (
              <span
                key={status}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${colors.bg} ${colors.text} ${colors.border}`}
              >
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: getStatusColors(status).dot }}
                />
                {status}
              </span>
            );
          })}
        </div>
      </div>

      {/* Widgets */}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-4">
          <SpotlightSection data={performanceData} isLoading={isLoading} />
        </div>
        <div className="col-span-8">
          <PerformanceQuadrant data={performanceData} />
        </div>
      </div>
      <LeaderboardSection data={performanceData} isLoading={isLoading} />
    </div>
  );
}

export default PerformanceDashboard;
